<script lang="ts">
/**
 * Navigation entry rendered by the sidebar rail.
 */
export interface HzSidebarItem {
  /** Stable identifier, emitted on navigation and compared with activeKey. */
  key: string;
  /** French label displayed next to the icon. */
  label: string;
  /**
   * Destination of the entry. Navigation must be a link, not a button: an advisor
   * expects to open a section in a new tab, and assistive technology must announce a
   * link. SPA routing still happens on a plain click.
   */
  href: string;
}
</script>

<script setup lang="ts">
defineProps<{
  /** The seven navigation entries of the product shell. */
  items: readonly HzSidebarItem[];
  /** Key of the entry currently rendered as active. */
  activeKey: string;
}>();

const emit = defineEmits<{
  /** Raised when an entry is activated by pointer or keyboard. */
  navigate: [key: string];
  /** Raised when the support call to action is activated. */
  contact: [];
}>();

/**
 * Routes inside the application on a plain click, and lets the browser do its job for
 * every modified click, so opening a section in a new tab keeps working.
 */
function surClic(evenement: MouseEvent, cle: string): void {
  if (
    evenement.defaultPrevented ||
    evenement.button !== 0 ||
    evenement.metaKey ||
    evenement.ctrlKey ||
    evenement.shiftKey ||
    evenement.altKey
  ) {
    return;
  }
  evenement.preventDefault();
  emit('navigate', cle);
}
</script>

<template>
  <aside class="hz-sidebar">
    <div class="hz-sidebar__logo">
      <span
        class="hz-sidebar__tile"
        aria-hidden="true"
      >W</span>
      <span class="hz-sidebar__brand">WeSave</span>
    </div>

    <nav
      class="hz-sidebar__nav"
      aria-label="Navigation principale"
    >
      <a
        v-for="item in items"
        :key="item.key"
        :href="item.href"
        class="hz-sidebar__item"
        :class="{ 'hz-sidebar__item--active': item.key === activeKey }"
        :aria-current="item.key === activeKey ? 'page' : undefined"
        @click="surClic($event, item.key)"
      >
        <span
          v-if="item.key === activeKey"
          class="hz-sidebar__rail"
          aria-hidden="true"
        />
        <svg
          class="hz-sidebar__icon"
          viewBox="0 0 17 17"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x="0.8"
            y="0.8"
            width="15.4"
            height="15.4"
            rx="5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>
        <span class="hz-sidebar__label">{{ item.label }}</span>
      </a>
    </nav>

    <div class="hz-sidebar__help">
      <p class="hz-sidebar__help-title">
        Besoin d’aide ?
      </p>
      <p class="hz-sidebar__help-text">
        Guide et support
      </p>
      <button
        type="button"
        class="hz-sidebar__cta"
        @click="emit('contact')"
      >
        Contacter
      </button>
    </div>
  </aside>
</template>

<style scoped>
.hz-sidebar {
  width: var(--hz-sidebar-w);
  flex: none;
  box-sizing: border-box;
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  background: var(--hz-bg-card);
  border-right: 1px solid var(--hz-border);
  font-family: var(--hz-font);
}

.hz-sidebar__logo {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 19px 20px 22px;
}

.hz-sidebar__tile {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--hz-primary-900);
  color: var(--hz-bg-card);
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 800;
}

.hz-sidebar__brand {
  font-size: var(--hz-fs-lg);
  font-weight: 800;
  color: var(--hz-primary-700);
  letter-spacing: -0.01em;
}

.hz-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hz-sidebar__item {
  position: relative;
  box-sizing: border-box;
  height: 40px;
  margin: 1px 12px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 11px;
  border: none;
  border-radius: var(--hz-radius);
  text-decoration: none;
  background: transparent;
  color: var(--hz-text-muted);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  user-select: none;
}

.hz-sidebar__item:hover:not(.hz-sidebar__item--active) {
  background: var(--hz-bg-app);
  color: var(--hz-text-strong);
}

.hz-sidebar__item:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}

.hz-sidebar__item--active {
  background: var(--hz-primary-100);
  color: var(--hz-primary-700);
  font-weight: 700;
  cursor: default;
}

.hz-sidebar__rail {
  position: absolute;
  left: -12px;
  top: 7px;
  width: 3px;
  height: 26px;
  border-radius: 0 3px 3px 0;
  background: var(--hz-primary-700);
}

.hz-sidebar__icon {
  width: 17px;
  height: 17px;
  flex: none;
}

.hz-sidebar__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hz-sidebar__help {
  margin: auto 12px 14px;
  padding: 14px;
  border-radius: 10px;
  background: var(--hz-bg-app);
}

.hz-sidebar__help-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--hz-text-strong);
}

.hz-sidebar__help-text {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: var(--hz-text-muted);
}

.hz-sidebar__cta {
  height: 28px;
  margin-top: 10px;
  padding: 0 16px;
  border: none;
  border-radius: var(--hz-radius-pill);
  background: var(--hz-primary-900);
  color: var(--hz-bg-card);
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
}

.hz-sidebar__cta:hover {
  background: var(--hz-primary-700);
}

.hz-sidebar__cta:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}
</style>
